// Libs
import classNames from 'classnames/bind';
import { Alert, Button, Flex, Spinner, Text } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
// Others
import authApi from '~/apis/auth';
import { useCanvasSessionExpired } from '~/utils/hooks/canvasSession';
// Styles, images, icons
import styles from './CanvasSessionNotice.module.scss';

const cx = classNames.bind(styles);

/**
 * One notice for the whole page when the backend can no longer renew the user's Canvas
 * authorization (code 60004, event sent by axiosClient): the only fix is a new Canvas login.
 */
const CanvasSessionNotice = () => {
  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  const isVisible = useCanvasSessionExpired();
  //#endregion Declare Hook

  //#region Declare State
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  //#endregion Declare State

  //#region Handle Function
  const handleLoginAgain = async () => {
    setIsRedirecting(true);
    let loginUrl = '/auth';
    try {
      const res = await authApi.getLoginUrl();
      loginUrl = res.data.data.url || loginUrl;
    } catch {
      // The sign-in page gets the Canvas URL by itself.
    }
    localStorage.clear();
    window.location.href = loginUrl;
  };
  //#endregion Handle Function

  if (!isVisible) return null;

  return (
    <div className={cx('notice')} role='alert'>
      <Alert variant='warning' margin='0' hasShadow>
        <Flex gap='small' alignItems='center' justifyItems='space-between' wrap='wrap'>
          <Text>{t('canvas_session_expired_message')}</Text>
          <Button color='primary' interaction={isRedirecting ? 'disabled' : 'enabled'} onClick={handleLoginAgain}>
            <Flex justifyItems='center' alignItems='center' gap='small'>
              <span>{t('canvas_session_expired_button')}</span>
              {isRedirecting && <Spinner size='x-small' />}
            </Flex>
          </Button>
        </Flex>
      </Alert>
    </div>
  );
};

export default CanvasSessionNotice;
